import type { ReactNode, Ref } from "react";
import { Link } from "react-router-dom";
import { BrandLogo, type ContactMessagePrefill } from "@dearbill/shared";
import { usePartnerSession } from "../auth/usePartnerSession";
import { usePartnerMe } from "../auth/usePartnerMe";
import { ContactSupportButton } from "../components/ContactSupportButton";

/**
 * Global top bar on every authenticated page, and the only place with the
 * signed-in identity link and sign-out.
 *
 * The brand link goes to the org list. "Need help? Call" opens the contact
 * modal, and the number beside it is a `tel:` link. "Signed in <name>" links to
 * `/profile`, showing the email when the name is missing and "Partner" while
 * `me` is absent. "Sign out" clears the session.
 */
export function TopBar({ tourSlotRef }: { tourSlotRef?: Ref<HTMLDivElement> }): ReactNode {
  const { session, clear } = usePartnerSession();
  const { me } = usePartnerMe();

  const displayName = me?.name ?? me?.email ?? "Partner";

  // Prefill the contact modal's message tab from `/me`: email is always
  // locked; phone is locked only when we have one on file (else the partner
  // types it). The call tab derives its phone prefill from this in
  // ContactSupportButton.
  const prefill: ContactMessagePrefill | undefined = me
    ? {
        email: me.email,
        emailReadOnly: true,
        ...(me.phone ? { phone: me.phone, phoneReadOnly: true } : {}),
      }
    : undefined;

  return (
    <header className="bg-deep-navy">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-3 text-sm text-white sm:px-7">
        <Link to="/" className="flex min-h-11 min-w-0 max-w-full flex-wrap items-center gap-3.5">
          <BrandLogo className="h-[26px] max-w-full brightness-0 invert" />
          <span className="text-white/75">Partner portal</span>
        </Link>

        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-x-6 gap-y-1">
          <div ref={tourSlotRef} className="empty:hidden" />
          <span className="flex min-w-0 max-w-full flex-wrap items-center gap-1.5">
            <ContactSupportButton
              label="Need help? Call"
              className="min-h-11 max-w-full whitespace-normal p-0 text-sm font-normal text-white [overflow-wrap:anywhere] hover:underline hover:opacity-100"
              {...(prefill ? { prefill } : {})}
              {...(session ? { token: session } : {})}
            />
            <a
              href="tel:+18552647130"
              className="inline-flex min-h-11 max-w-full items-center font-medium [overflow-wrap:anywhere] hover:underline"
            >
              855-264-7130
            </a>
          </span>
          <Link
            to="/profile"
            className="inline-flex min-h-11 min-w-0 max-w-full items-center [overflow-wrap:anywhere] hover:underline"
          >
            Signed in {displayName}
          </Link>
          <button
            type="button"
            onClick={clear}
            className="min-h-11 max-w-full cursor-pointer font-medium underline underline-offset-2 hover:opacity-80"
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
